/* DTD Build-your-own-box — works with Expanse cart drawer (ajaxProduct:added). */
(() => {
  if (customElements.get('dtd-byob')) return;

  const fmtNumber = (cents, decimals, thousands = ',', decimal = '.') => {
    const fixed = (cents / 100).toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  };

  const formatMoney = (cents, format) => {
    const fmt = (format || '{{amount}}').replace(/<[^>]*>/g, '');
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const c = Math.max(0, Math.round(cents));
    const map = {
      amount: () => fmtNumber(c, 2),
      amount_no_decimals: () => fmtNumber(c, 0),
      amount_with_comma_separator: () => fmtNumber(c, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmtNumber(c, 0, '.', ','),
      amount_with_apostrophe_separator: () => fmtNumber(c, 2, "'", '.'),
      amount_with_space_separator: () => fmtNumber(c, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => fmtNumber(c, 0, ' ', ',')
    };
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, (map[key] || map.amount)());
  };

  const fill = (tpl, vars) =>
    String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));

  const storage = {
    get(key) { try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; } },
    set(key, val) { try { sessionStorage.setItem(key, JSON.stringify(val)); } catch (e) {} },
    del(key) { try { sessionStorage.removeItem(key); } catch (e) {} }
  };

  class DtdByob extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.cfg = JSON.parse(this.querySelector('[data-byob-config]').textContent);
      } catch (e) {
        console.error('[DTD BYOB] bad config', e);
        return;
      }
      this.cfg.max = Math.max(this.cfg.max, this.cfg.min);
      this.tiers = (this.cfg.tiers || [])
        .filter((t) => t.qty > 0 && t.value > 0)
        .sort((a, b) => a.qty - b.qty);
      this.storageKey = 'dtd-byob:' + this.cfg.sectionId;
      this.items = []; // [{variantId, productId, title, variantTitle, price, thumb, qty}]
      this.cards = [...this.querySelectorAll('[data-byob-card]')];

      this.bindCards();
      this.bindTabs();
      this.bindTray();
      this.renderMilestones();
      this.restore();
      this.render();
    }

    /* ---------- state ---------- */
    get count() { return this.items.reduce((n, i) => n + i.qty, 0); }
    get subtotal() { return this.items.reduce((n, i) => n + i.qty * i.price, 0); }
    productCount(pid) { return this.items.filter((i) => i.productId === pid).reduce((n, i) => n + i.qty, 0); }

    activeTier(count = this.count) {
      let tier = null;
      this.tiers.forEach((t) => { if (count >= t.qty) tier = t; });
      return count >= this.cfg.min ? tier : null;
    }
    nextTier(count = this.count) { return this.tiers.find((t) => t.qty > count && t.qty <= this.cfg.max) || null; }
    tierLabel(t) {
      if (!t) return '';
      return this.cfg.discountType === 'percentage'
        ? `${t.value}% off`
        : `${this.money(t.value * 100).replace(/[.,]00(?=\D*$)/, '')} off`;
    }
    discountFor(t, subtotal = this.subtotal) {
      if (!t) return 0;
      const d = this.cfg.discountType === 'percentage' ? (subtotal * t.value) / 100 : t.value * 100;
      return Math.min(subtotal, Math.round(d));
    }
    money(c) { return formatMoney(c, this.cfg.moneyFormat); }

    canAdd(pid) {
      if (this.count >= this.cfg.max) return false;
      if (this.cfg.maxPerProduct > 0 && this.productCount(pid) >= this.cfg.maxPerProduct) return false;
      return true;
    }

    add(card) {
      const pid = card.dataset.productId;
      if (!this.canAdd(pid)) { this.flash(); return; }
      const opt = this.selectedOption(card);
      if (!opt || opt.disabled) return;
      const vid = opt.value;
      const existing = this.items.find((i) => i.variantId === vid);
      if (existing) existing.qty += 1;
      else {
        this.items.push({
          variantId: vid,
          productId: pid,
          title: card.dataset.productTitle,
          variantTitle: opt.dataset.title === 'Default Title' ? '' : opt.dataset.title,
          price: parseInt(opt.dataset.price, 10) || 0,
          thumb: opt.dataset.thumb || '',
          qty: 1
        });
      }
      this.render(true);
    }

    remove(vid, all = false) {
      const item = this.items.find((i) => i.variantId === String(vid));
      if (!item) return;
      item.qty = all ? 0 : item.qty - 1;
      this.items = this.items.filter((i) => i.qty > 0);
      this.render(true);
    }

    save() {
      if (!this.cfg.remember) return;
      if (this.items.length) storage.set(this.storageKey, this.items);
      else storage.del(this.storageKey);
    }

    restore() {
      const saved = this.cfg.remember ? storage.get(this.storageKey) : null;
      if (Array.isArray(saved) && saved.length) {
        // only keep variants still present on the page
        const valid = new Set();
        this.cards.forEach((c) => c.querySelectorAll('option').forEach((o) => { if (!o.disabled) valid.add(o.value); }));
        this.items = saved.filter((i) => valid.has(String(i.variantId))).slice(0);
        let total = 0;
        this.items = this.items.filter((i) => { total += i.qty; return total <= this.cfg.max; });
        if (this.items.length) return;
      }
      if (this.cfg.preaddVariant) {
        const card = this.querySelector('[data-current-product]');
        if (card) {
          const sel = card.querySelector('[data-byob-variant]');
          if (sel && [...sel.options].some((o) => o.value === String(this.cfg.preaddVariant) && !o.disabled)) {
            sel.value = String(this.cfg.preaddVariant);
          }
          if (card.querySelector('[data-byob-add]')) this.add(card);
        }
      }
    }

    /* ---------- cards ---------- */
    selectedOption(card) {
      const sel = card.querySelector('[data-byob-variant]');
      return sel ? sel.options[sel.selectedIndex] : null;
    }

    bindCards() {
      this.cards.forEach((card) => {
        card.querySelector('[data-byob-add]')?.addEventListener('click', () => this.add(card));
        card.querySelector('[data-byob-inc]')?.addEventListener('click', () => this.add(card));
        card.querySelector('[data-byob-dec]')?.addEventListener('click', () => {
          const opt = this.selectedOption(card);
          if (opt) this.remove(opt.value);
        });
        const sel = card.querySelector('[data-byob-variant]');
        sel?.addEventListener('change', () => {
          const opt = this.selectedOption(card);
          const priceEl = card.querySelector('[data-byob-price]');
          const cmpEl = card.querySelector('[data-byob-compare]');
          const price = parseInt(opt.dataset.price, 10) || 0;
          const cmp = parseInt(opt.dataset.compare, 10) || 0;
          if (priceEl) priceEl.textContent = this.money(price);
          if (cmpEl) { cmpEl.textContent = this.money(cmp); cmpEl.hidden = !(cmp > price); }
          const cmpLbl = card.querySelector('[data-byob-compare-label]');
          if (cmpLbl) cmpLbl.hidden = !(cmp > price);
          const priceLbl = card.querySelector('[data-byob-price-label]');
          if (priceLbl) priceLbl.textContent = cmp > price ? 'Sale price' : 'Price';
          const img = card.querySelector('.dtd-byob-card__img');
          if (img && opt.dataset.image) { img.src = opt.dataset.image; img.removeAttribute('srcset'); }
          this.renderCard(card);
        });
      });
    }

    renderCard(card) {
      const pid = card.dataset.productId;
      const opt = this.selectedOption(card);
      const vid = opt ? opt.value : null;
      const item = this.items.find((i) => i.variantId === vid);
      const qty = item ? item.qty : 0;
      const pTotal = this.productCount(pid);
      const addBtn = card.querySelector('[data-byob-add]');
      const stepper = card.querySelector('[data-byob-stepper]');
      const badge = card.querySelector('[data-byob-card-count]');
      if (!addBtn) return;
      const blocked = !this.canAdd(pid) || (opt && opt.disabled);
      addBtn.hidden = qty > 0;
      addBtn.disabled = blocked;
      stepper.hidden = qty === 0;
      stepper.querySelector('[data-byob-qty]').textContent = qty;
      stepper.querySelector('[data-byob-inc]').disabled = blocked;
      card.classList.toggle('is-in-box', pTotal > 0);
      if (badge) { badge.hidden = pTotal === 0; badge.textContent = '×' + pTotal; }
    }

    /* ---------- tabs ---------- */
    bindTabs() {
      const tabs = [...this.querySelectorAll('[data-byob-tab]')];
      if (!tabs.length) return;
      const apply = (tab) => {
        tabs.forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
        const ids = tab.dataset.byobTab;
        this.cards.forEach((c) => {
          const show = ids === 'all' || c.hasAttribute('data-current-product') || ids.includes(',' + c.dataset.productId + ',');
          c.hidden = !show;
        });
      };
      tabs.forEach((t) => t.addEventListener('click', () => apply(t)));
      apply(tabs.find((t) => t.getAttribute('aria-pressed') === 'true') || tabs[0]);

      // Theme editor: selecting a Tab block shows that tab
      this._onBlockSelect = (e) => {
        const tab = tabs.find((t) => t.dataset.blockId === e.detail?.blockId);
        if (tab) apply(tab);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    disconnectedCallback() {
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._observer) this._observer.disconnect();
    }

    /* ---------- tray ---------- */
    bindTray() {
      this.tray = this.querySelector('[data-byob-tray]');
      this.toggleBtn = this.querySelector('[data-byob-toggle]');
      this.toggleBtn?.addEventListener('click', () => this.setOpen(!this.tray.classList.contains('is-open')));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.tray.classList.contains('is-open')) {
          this.setOpen(false);
          if (this.toggleBtn && getComputedStyle(this.toggleBtn).display !== 'none') this.toggleBtn.focus();
        }
      });
      this.querySelector('[data-byob-submit]')?.addEventListener('click', () => this.submit());
      this.giftInput = this.querySelector('[data-byob-gift]');
      this.giftInput?.addEventListener('change', () => this.render());

      // Show the mobile bar only while the section is on screen
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-in-view', entry.isIntersecting);
          if (!entry.isIntersecting) this.setOpen(false);
        }, { rootMargin: '0px 0px -30% 0px' }).observe(this);
      } else this.classList.add('is-in-view');
    }

    setOpen(open) {
      if (!this.tray) return;
      this.tray.classList.toggle('is-open', open);
      this.toggleBtn?.setAttribute('aria-expanded', String(open));
    }

    renderMilestones() {
      const wrap = this.querySelector('[data-byob-milestones]');
      const chips = this.querySelector('[data-byob-chips]');
      if (!wrap) return;
      const max = this.cfg.max;
      const tiers = this.tiers.filter((t) => t.qty <= max);
      wrap.innerHTML = '';
      if (chips) chips.innerHTML = '';
      tiers.forEach((t) => {
        const m = document.createElement('span');
        m.className = 'dtd-byob__milestone';
        m.dataset.qty = t.qty;
        m.style.left = `${(t.qty / max) * 100}%`;
        m.textContent = t.qty;
        wrap.appendChild(m);
        if (chips) {
          const c = document.createElement('li');
          c.dataset.qty = t.qty;
          c.innerHTML = '<b></b><span></span>';
          c.querySelector('b').textContent = t.qty + ' items';
          c.querySelector('span').textContent = this.tierLabel(t);
          chips.appendChild(c);
        }
      });
      if (chips) chips.hidden = tiers.length === 0;
    }

    render(changed = false) {
      const count = this.count;
      const { min, max, msg } = this.cfg;
      const subtotal = this.subtotal;
      const tier = this.activeTier();
      const next = this.nextTier();
      const discount = this.discountFor(tier);
      const gift = this.giftInput?.checked ? this.giftPrice() : 0;
      const total = subtotal - discount + gift;

      this.cards.forEach((c) => this.renderCard(c));
      this.querySelectorAll('[data-byob-count]').forEach((el) => (el.textContent = count));

      // progress
      const fillEl = this.querySelector('[data-byob-fill]');
      if (fillEl) fillEl.style.width = `${Math.min(100, (count / max) * 100)}%`;
      this.querySelectorAll('.dtd-byob__milestone, [data-byob-chips] li').forEach((m) => m.classList.toggle('is-hit', count >= +m.dataset.qty && count >= min));
      const activeQty = tier ? tier.qty : -1;
      this.querySelectorAll('[data-byob-chips] li').forEach((c) => c.classList.toggle('is-active', +c.dataset.qty === activeQty));

      // message
      let text;
      if (count < min) text = fill(msg.min, { remaining: min - count, count });
      else if (count >= max) text = tier ? fill(msg.best, { discount: this.tierLabel(tier), count }) + ' · ' + msg.full : msg.full;
      else if (next) text = fill(msg.next, { remaining: next.qty - count, discount: this.tierLabel(next), count });
      else if (tier) text = fill(msg.best, { discount: this.tierLabel(tier), count });
      else text = fill(msg.ready, { count });
      const msgEl = this.querySelector('[data-byob-message]');
      if (msgEl) msgEl.textContent = text;
      const barText = this.querySelector('[data-byob-bar-text]');
      if (barText) barText.textContent = text;

      // slots
      this.renderSlots();

      // totals
      const set = (sel, v) => { const el = this.querySelector(sel); if (el) el.textContent = v; };
      set('[data-byob-subtotal]', count ? this.money(subtotal + gift) : '—');
      const savingRow = this.querySelector('[data-byob-saving-row]');
      if (savingRow) savingRow.hidden = discount <= 0;
      set('[data-byob-saving]', `${this.tierLabel(tier)} · −${this.money(discount)}`);
      set('[data-byob-total]', count ? this.money(total) : '—');
      set('[data-byob-bar-total]', count ? this.money(total) : '');

      // CTA
      const btn = this.querySelector('[data-byob-submit]');
      const label = this.querySelector('[data-byob-cta-label]');
      const ready = count >= min;
      if (btn && !this._busy) {
        btn.disabled = !ready;
        label.textContent = ready
          ? `${fill(msg.cta, { count })} · ${this.money(total)}`
          : fill(msg.ctaLocked, { remaining: min - count, count });
      }
      this.classList.toggle('has-items', count > 0);
      this.classList.toggle('is-full', count >= max);

      if (changed) {
        this.save();
        this.bump();
      }
    }

    renderSlots() {
      const list = this.querySelector('[data-byob-slots]');
      if (!list) return;
      const units = [];
      this.items.forEach((i) => { for (let n = 0; n < i.qty; n++) units.push(i); });
      list.innerHTML = '';
      for (let s = 0; s < this.cfg.max; s++) {
        const li = document.createElement('li');
        const unit = units[s];
        if (unit) {
          li.className = 'dtd-byob__slot is-filled';
          const name = unit.variantTitle ? `${unit.title} — ${unit.variantTitle}` : unit.title;
          li.innerHTML = `
            ${unit.thumb ? `<img src="${unit.thumb}" alt="" loading="lazy">` : '<span class="dtd-byob__slot-ph"></span>'}
            <button type="button" aria-label="Remove ${name.replace(/"/g, '&quot;')}" data-remove="${unit.variantId}">×</button>
            <span class="dtd-byob__slot-name"></span>`;
          li.querySelector('.dtd-byob__slot-name').textContent = name;
          li.querySelector('button').addEventListener('click', () => this.remove(unit.variantId));
        } else {
          li.className = 'dtd-byob__slot' + (s < this.cfg.min ? ' is-required' : '');
          li.innerHTML = `<span aria-hidden="true">${s + 1}</span>`;
        }
        list.appendChild(li);
      }
    }

    giftPrice() { return parseInt(this.cfg.giftPrice, 10) || 0; }

    bump() {
      const bar = this.querySelector('[data-byob-toggle]');
      if (!bar) return;
      bar.classList.remove('is-bump');
      void bar.offsetWidth;
      bar.classList.add('is-bump');
    }

    flash() {
      const msgEl = this.querySelector('[data-byob-message]');
      msgEl?.classList.remove('is-flash');
      void msgEl?.offsetWidth;
      msgEl?.classList.add('is-flash');
      if (this.count >= this.cfg.max && msgEl) msgEl.textContent = this.cfg.msg.full;
    }

    /* ---------- cart ---------- */
    async post(path, body) {
      const res = await fetch(this.cfg.root + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add the box to cart');
      return data;
    }

    async submit() {
      if (this._busy || this.count < this.cfg.min) return;
      this._busy = true;
      const btn = this.querySelector('[data-byob-submit]');
      const label = this.querySelector('[data-byob-cta-label]');
      const errEl = this.querySelector('[data-byob-error]');
      btn.disabled = true;
      btn.classList.add('is-loading');
      errEl.hidden = true;

      const bundleId = 'BOX-' + Date.now().toString(36).toUpperCase();
      const tier = this.activeTier();
      const props = { [this.cfg.propertyName]: this.cfg.bundleTitle, _bundle_id: bundleId };
      if (tier) props._bundle_tier = this.tierLabel(tier);
      const items = this.items.map((i) => ({ id: Number(i.variantId), quantity: i.qty, properties: { ...props } }));
      if (this.giftInput?.checked && this.cfg.giftVariant) {
        items.push({ id: Number(this.cfg.giftVariant), quantity: 1, properties: { ...props } });
      }
      const wantsDrawer = this.cfg.afterAdd === 'drawer' && this.cfg.cartType === 'dropdown';

      try {
        let response;
        const code = tier && tier.code ? tier.code : '';
        if (code) {
          await this.post('cart/add.js', { items });
          response = await this.applyCode(code, wantsDrawer);
        } else {
          response = await this.post('cart/add.js', wantsDrawer ? { items, sections: 'cart-ajax' } : { items });
        }

        this.items = [];
        if (this.giftInput) this.giftInput.checked = false;
        storage.del(this.storageKey);
        this.setOpen(false);

        if (!wantsDrawer) {
          window.location.href = this.cfg.root + (this.cfg.afterAdd === 'checkout' ? 'checkout' : 'cart');
          return;
        }

        this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { product: response, preventCartOpen: false }
        }));
        document.dispatchEvent(new CustomEvent('dtd-byob:added', { detail: { bundleId, tier, items } }));
        label.textContent = this.cfg.msg.added;
        setTimeout(() => { this._busy = false; btn.classList.remove('is-loading'); this.render(); }, 1600);
        this.render();
        return;
      } catch (err) {
        errEl.textContent = err.message;
        errEl.hidden = false;
      }
      this._busy = false;
      btn.classList.remove('is-loading');
      this.render();
    }

    // Apply tier code while keeping any non-bundle codes already on the cart.
    async applyCode(code, withSections) {
      const tierCodes = this.tiers.map((t) => (t.code || '').toUpperCase()).filter(Boolean);
      let existing = [];
      try {
        const cart = await (await fetch(this.cfg.root + 'cart.js')).json();
        existing = (cart.discount_codes || [])
          .map((d) => d.code)
          .filter((c) => c && !tierCodes.includes(c.toUpperCase()));
      } catch (e) {}
      const body = { discount: [...existing, code].join(',') };
      if (withSections) body.sections = 'cart-ajax';
      try {
        return await this.post('cart/update.js', body);
      } catch (e) {
        // Fallback: legacy shareable discount link sets the code for checkout.
        await fetch(this.cfg.root + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(() => {});
        return this.post('cart/update.js', withSections ? { sections: 'cart-ajax' } : {});
      }
    }
  }

  customElements.define('dtd-byob', DtdByob);
})();
